import * as React from 'react';
import { useContext, useState } from 'react';
import { TEDSelectionOperator } from '../../../../../../globalQueryTypes';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  dateToString,
  firstDayInMonth,
  firstDayInWeek,
  incDay,
} from '../../../../../../Utils/Formatters/Utils/DateTimeParserUtils';
import KalenderResourceSelectDialog from './KalenderResourceSelectDialog';
import { IUrvalButtonProps } from '../../../../../../Components/Urval/Components/UrvalButton/UrvalButton';
import {
  IStringConditionObject,
  IUIUrval,
  IUrvalCondition,
} from '../../../../../../Types/UrvalTypes';
import {
  getAttributeInfo,
  findAssociations,
} from '../../../../../../Model/avenyModelUtils';
import shortid from 'shortid';
import {
  ModelContext,
  AvenyCollectionMap,
} from '../../../../../../Model/ModelContext/ModelContext';
import { IResourceInfo } from '../Utils/KalenderTypes';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { useQuery } from '@apollo/client';
import {
  MeQuery,
  MeQueryVariables,
} from '../Queries/MeQuery/_QueryTypes/MeQuery';
import meQuery from '../Queries/MeQuery/MeQuery.gql';
import { RutinSettingsContext } from '../../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { ViewDbSettingsContext } from './SchedulerComponent/ViewDbSettingsContext';
import { ResursTypContextProvider } from '../../../Contexts/ResursTypContext/ResursTypContext';

const makeCondition = (
  attributePath: string,
  condition: IUrvalCondition,
  avenyCollectionMap: AvenyCollectionMap
): IStringConditionObject => ({
  type: 'String',
  id: shortid(),
  attribute: getAttributeInfo(
    'BOBokningsrubrik',
    attributePath,
    avenyCollectionMap
  ).attribute,
  modelPath: findAssociations(
    'BOBokningsrubrik',
    attributePath.split('.').slice(0, -1),
    avenyCollectionMap
  ),
  condition: [condition],
});

const getDatumRange = (
  currentDate: Date,
  currentView: string
): { value: string; toValue: string } => {
  if (currentView === 'Month' || currentView === 'Month2') {
    const start = firstDayInWeek(firstDayInMonth(currentDate));
    return {
      value: dateToString(start),
      toValue: dateToString(incDay(start, 41)),
    };
  }
  if (currentView === 'List') {
    const weekStart = firstDayInWeek(currentDate);
    return {
      value: dateToString(incDay(weekStart, -365)),
      toValue: dateToString(incDay(weekStart, 365)),
    };
  }
  const start = firstDayInWeek(currentDate);
  return {
    value: dateToString(start),
    toValue: dateToString(incDay(start, 6)),
  };
};

export const makeSelection = (
  checkedResources: IResourceInfo[] | undefined,
  defaultResource: string,
  currentDate: Date,
  currentView: string,
  avenyCollectionMap: AvenyCollectionMap
): IUIUrval => {
  const resourceCondition =
    checkedResources && checkedResources.length > 0
      ? makeCondition(
          'Resurs.BO_RESURS_ID',
          {
            id: shortid(),
            op: TEDSelectionOperator.equals,
            values: checkedResources.map(resInfo => ({
              id: shortid(),
              value: resInfo.BO_RESURS_ID ?? '',
            })),
          },
          avenyCollectionMap
        )
      : makeCondition(
          'Resurs.Anvandare.SIGNATURE',
          {
            id: shortid(),
            op: TEDSelectionOperator.equals,
            values: [{ id: shortid(), value: defaultResource }],
          },
          avenyCollectionMap
        );

  const DatumRange = getDatumRange(currentDate, currentView);

  // Gör om intervall till öppet intervall för att få med överlappande

  const tillDatumCondition = makeCondition(
    'TILLDATUM',
    {
      id: shortid(),
      op: TEDSelectionOperator.notless,
      values: [{ id: shortid(), value: DatumRange.value }],
    },
    avenyCollectionMap
  );

  const franDatumCondition = makeCondition(
    'FRANDATUM',
    {
      id: shortid(),
      op: TEDSelectionOperator.notgreater,
      values: [{ id: shortid(), value: DatumRange.toValue }],
    },
    avenyCollectionMap
  );

  return {
    pages: [
      {
        id: shortid(),
        conditionObjects: [
          resourceCondition,
          franDatumCondition,
          tillDatumCondition,
        ],
      },
    ],
  };
};

export const defaultKalenderProps = {
  currentDate: new Date(),
  currentView: 'Week',
  getNyAktivitetInitialValues: () => undefined,
};

export const KalenderCustomUrvalButton: React.FunctionComponent<
  React.PropsWithChildren<IUrvalButtonProps>
> = props => {
  const { avenyCollectionMap } = useContext(ModelContext);
  const loginStateContext = useContext(LoginStateContext);
  const activeDataBaseContext = useContext(ActiveDatabaseContext);
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const viewDbSettingsContext = useContext(ViewDbSettingsContext);

  const { data: me_data, loading: me_loading } = useQuery<
    MeQuery,
    MeQueryVariables
  >(meQuery, {
    variables: {
      dbname: activeDataBaseContext.activeDatabase ?? '',
    },
    skip: !open || !activeDataBaseContext.activeDatabase,
  });

  const [newBookingGroup, setNewBookingGroup] = useState<string>(
    routineAvailabilityContext.currentBookingGroup ?? ''
  );

  const {
    currentDate = defaultKalenderProps.currentDate,
    currentView = defaultKalenderProps.currentView,
  } = viewDbSettingsContext;

  return (
    <ResursTypContextProvider>
      <KalenderResourceSelectDialog
        expanded={props.expanded}
        onSelect={checkedResources => {
          viewDbSettingsContext.setResources(checkedResources);
          if (props.setUrval)
            props.setUrval(
              makeSelection(
                checkedResources,
                loginStateContext.sign,
                currentDate,
                currentView,
                avenyCollectionMap
              )
            );
        }}
        routineID={props.routineID}
        activeDataBaseContext={activeDataBaseContext}
        me_data={me_data}
        me_loading={me_loading}
        newBookingGroup={newBookingGroup}
        setNewBookingGroup={setNewBookingGroup}
        renderButton={props.renderButton}
      />
    </ResursTypContextProvider>
  );
};
